import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { PORTAL_SECTIONS, portalSearch } from './clientPortalNavigation';
import { fullName } from './clientPortalSelectors';

const sections = [...PORTAL_SECTIONS, ['school-calendar', 'Kalendarz szkoły']];

export default function ClientPortalShell({ section, students, selectedStudentId, busy, identity, canSwitch, onSwitch, onSignOut, onNavigate, onStudent, headingRef, children }) {
  const [accountOpen, setAccountOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const accountButton = useRef(null);
  const moreButton = useRef(null);
  const sectionLabel = sections.find(([id]) => id === section)[1];
  const closeAccount = event => {
    if (event.key === 'Escape') { setAccountOpen(false); accountButton.current.focus(); }
  };
  const closeMore = event => {
    if (event.key === 'Escape' && moreOpen) { setMoreOpen(false); moreButton.current.focus(); }
  };
  const navLink = (id, label, className) => (
    <Link
      key={id}
      className={className}
      to={id === 'school-calendar' ? '/client/school-calendar' : `/client${portalSearch(id, selectedStudentId)}`}
      aria-current={section === id ? 'page' : undefined}
      onKeyDown={closeMore}
      onClick={() => { setMoreOpen(false); onNavigate(id); }}
    >{label}
    </Link>
  );
  return (
    <div className="client-portal">
      <header className="client-portal__header fixed-top">
        <Link className="client-portal__brand" to="/">Extra English <span>Portal klienta</span></Link>
        <div className="client-portal__header-actions">
          <Link to="/" className="client-portal__school-link">← Strona szkoły</Link>
          <div
            className="client-portal__account"
          >
            <button ref={accountButton} onKeyDown={closeAccount} type="button" aria-expanded={accountOpen} aria-controls="portal-account" onClick={() => setAccountOpen(!accountOpen)}>Konto</button>
            {accountOpen && (
            <div id="portal-account" className="client-portal__account-panel">
              <div>{identity}</div>
              {canSwitch && <button type="button" disabled={busy} onKeyDown={closeAccount} onClick={onSwitch}>Przełącz konto</button>}
              <button type="button" onKeyDown={closeAccount} onClick={onSignOut}>Wyloguj</button>
            </div>
            )}
          </div>
        </div>
      </header>
      <main className="fixed-header-page client-portal__layout">
        <nav className="client-portal__sidebar" aria-label="Sekcje portalu">{sections.map(([id, label]) => navLink(id, label))}</nav>
        <div className="client-portal__content">
          <nav
            className="client-portal__mobile-nav"
            aria-label="Nawigacja mobilna"
          >
            <div className="client-portal__mobile-tabs">
              {navLink('dashboard', 'Pulpit')}{navLink('schedule', 'Lekcje')}
              <button ref={moreButton} onKeyDown={closeMore} type="button" aria-expanded={moreOpen} aria-controls="portal-more" onClick={() => setMoreOpen(!moreOpen)}>Więcej</button>
            </div>
            {moreOpen && <div id="portal-more" className="client-portal__more">{sections.slice(2).map(([id, label]) => navLink(id, label))}</div>}
          </nav>
          <div className="client-portal__page-title"><h1 ref={headingRef} tabIndex="-1">{section === 'dashboard' ? 'Twój pulpit' : sectionLabel}</h1>
            {section !== 'dashboard' && navLink('dashboard', '← Powrót do pulpitu', 'btn manage-teal-button')}
          </div>
          {students.length === 1 && <p className="client-portal__student-name">Uczeń: <strong>{fullName(students[0])}</strong></p>}
          {students.length > 1 && (
          <div className="client-portal__student-selector">
            <label htmlFor="portal-student">Uczniowie
              <select id="portal-student" className="form-control" value={selectedStudentId || ''} disabled={busy} onChange={event => onStudent(event.target.value || null)}>
                <option value="">Wszyscy uczniowie</option>{students.map(student => <option key={student.id} value={student.id}>{fullName(student)}</option>)}
              </select>
            </label>
          </div>
          )}
          <span className="sr-only" role="status">{selectedStudentId ? `Wybrany uczeń: ${fullName(students.find(student => student.id === selectedStudentId))}` : 'Wszyscy uczniowie'}</span>
          {children}
        </div>
      </main>
    </div>
  );
}
ClientPortalShell.propTypes = {
  section: PropTypes.string.isRequired,
  students: PropTypes.array.isRequired,
  selectedStudentId: PropTypes.string,
  busy: PropTypes.bool.isRequired,
  identity: PropTypes.string.isRequired,
  canSwitch: PropTypes.bool.isRequired,
  onSwitch: PropTypes.func.isRequired,
  onSignOut: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onStudent: PropTypes.func.isRequired,
  headingRef: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
};
ClientPortalShell.defaultProps = { selectedStudentId: null };
